import {
  type ChoiceResponse,
  type CreateGameResponse,
  ERROR_MESSAGES_FI,
  type ErrorCode,
  type GameStateResponse,
  type HostGamesResponse,
  isErrorCode,
  type JoinGameResponse,
  PLAYER_TOKEN_HEADER,
  type RoundResultResponse,
} from "@kalaporssi/shared";

export class ApiClientError extends Error {
  constructor(
    readonly code: ErrorCode | "NETWORK",
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = "ApiClientError";
  }
}

export interface ApiClientOptions {
  baseUrl: string;
  /** Cognito access token for host routes. */
  getHostToken?: () => Promise<string | null> | string | null;
  /** Player token for this game, from localStorage. */
  getPlayerToken?: (code: string) => string | null;
  fetch?: typeof fetch;
}

/** Typed wrapper over the REST API (SPEC §8). Errors surface as ApiClientError with the server's code. */
export function createApiClient(opts: ApiClientOptions) {
  const base = opts.baseUrl.replace(/\/+$/, "");
  const doFetch = opts.fetch ?? ((...a: Parameters<typeof fetch>) => fetch(...a));

  async function call<T>(
    method: string,
    path: string,
    o: { body?: unknown; host?: boolean; playerCode?: string } = {},
  ): Promise<T> {
    const headers: Record<string, string> = {};
    if (o.body !== undefined) headers["content-type"] = "application/json";
    if (o.host) {
      const t = await opts.getHostToken?.();
      if (t) headers.authorization = `Bearer ${t}`;
    }
    if (o.playerCode) {
      const t = opts.getPlayerToken?.(o.playerCode);
      if (t) headers[PLAYER_TOKEN_HEADER] = t;
    }
    let res: Response;
    try {
      res = await doFetch(`${base}${path}`, {
        method,
        headers,
        body: o.body === undefined ? undefined : JSON.stringify(o.body),
      });
    } catch {
      throw new ApiClientError("NETWORK", 0, "Yhteysvirhe. Tarkista verkkoyhteys.");
    }
    const text = await res.text();
    const data = text ? (JSON.parse(text) as unknown) : null;
    if (!res.ok) {
      const err = (data as { error?: { code?: unknown; message?: unknown } } | null)?.error;
      const code: ErrorCode = isErrorCode(err?.code) ? err.code : res.status === 401 ? "UNAUTHORIZED" : "INTERNAL";
      throw new ApiClientError(
        code,
        res.status,
        typeof err?.message === "string" ? err.message : ERROR_MESSAGES_FI[code],
      );
    }
    return data as T;
  }

  const c = (code: string) => encodeURIComponent(code);
  return {
    createGame: (aiPlayers: string[]) =>
      call<CreateGameResponse>("POST", "/games", { body: { aiPlayers }, host: true }),
    listHostGames: () => call<HostGamesResponse>("GET", "/host/games", { host: true }),
    joinGame: (code: string, nickname: string) =>
      call<JoinGameResponse>("POST", `/games/${c(code)}/players`, { body: { nickname } }),
    getGame: (code: string) => call<GameStateResponse>("GET", `/games/${c(code)}`, { playerCode: code }),
    startGame: (code: string) => call<GameStateResponse>("POST", `/games/${c(code)}/start`, { host: true }),
    openRound: (code: string) => call<GameStateResponse>("POST", `/games/${c(code)}/rounds`, { host: true }),
    submitChoice: (code: string, roundNo: number, catchIndex: number) =>
      call<ChoiceResponse>("PUT", `/games/${c(code)}/rounds/${roundNo}/choice`, {
        body: { catchIndex },
        playerCode: code,
      }),
    getResult: (code: string, roundNo: number) =>
      call<RoundResultResponse>("GET", `/games/${c(code)}/rounds/${roundNo}/result`),
    removePlayer: (code: string, playerId: string) =>
      call<GameStateResponse>("DELETE", `/games/${c(code)}/players/${encodeURIComponent(playerId)}`, { host: true }),
    retryResolve: (code: string, roundNo: number) =>
      call<GameStateResponse>("POST", `/games/${c(code)}/rounds/${roundNo}/resolve`, { host: true }),
  };
}

export type ApiClient = ReturnType<typeof createApiClient>;
